import { memo, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { RegisterUserType } from "@/domain/core";
import type { UserInfo } from "@/types/core";
import {
  type PracticeRegistrationData,
  type ProfessionalRegistrationData
} from "@/domain";
import { cn, components } from "@/utilities/tailwind-utils";
import { CardSubHeader } from "@/components/ui";
import { getErrorMessages } from "@/utilities/api-utils";
import {
  useSkillsFromLookupContext,
  useSpecialtiesFromLookupContext,
} from "@/context/use-lookups";

// ============================================================================
// REGISTRATION REVIEW TYPES
// ============================================================================

interface RegistrationReviewProps {
  registrationDataPractice: PracticeRegistrationData;
  registrationDataProfessional: ProfessionalRegistrationData;
  selectedUserType: RegisterUserType;
  userInfo: UserInfo;
  onSubmit: () => void;
  onBack: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error: unknown;
}

// ============================================================================
// REGISTRATION REVIEW COMPONENT
// ============================================================================

export const RegistrationReview = memo(function RegistrationReview({
  selectedUserType,
  registrationDataPractice,
  registrationDataProfessional,
  userInfo,
  onSubmit,
  onBack,
  onCancel,
  isSubmitting,
  error
}: RegistrationReviewProps) {
  const skillsLookup = useSkillsFromLookupContext();
  const specialtiesLookup = useSpecialtiesFromLookupContext();
  const skillTitleById = useMemo(
    () => new Map(skillsLookup.map((s) => [s.id, s.title])),
    [skillsLookup]
  );
  const specialtyTitleById = useMemo(
    () => new Map(specialtiesLookup.map((s) => [s.id, s.title])),
    [specialtiesLookup]
  );

  const professionalNode = useMemo(() => {
    return (
      <div className="space-y-6">
        {/* PERSONAL INFORMATION SECTION */}
        <div className="space-y-4">
          <CardSubHeader title="PERSONAL INFORMATION" as="h4" />
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">First Name:</span>
              <p>{registrationDataProfessional.firstName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Name:</span>
              <p>{registrationDataProfessional.lastName}</p>
            </div>
            {registrationDataProfessional.phone && (
              <div>
                <span className="text-muted-foreground">Mobile Number:</span>
                <p>{registrationDataProfessional.phone}</p>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Email Address:</span>
              <p>{userInfo.email}</p>
            </div>
          </div>
        </div>

        {/* PROFESSIONAL PROFILE SECTION */}
        <div className="space-y-4">
          <CardSubHeader title="PROFESSIONAL PROFILE" as="h4" />
          <div className="space-y-4 text-sm">
            {registrationDataProfessional.bio && (
              <div>
                <span className="text-muted-foreground">Bio:</span>
                <p className="mt-1">{registrationDataProfessional.bio}</p>
              </div>
            )}
            {registrationDataProfessional.designation && (
              <div>
                <span className="text-muted-foreground">Roles Open To:</span>
                <p>{registrationDataProfessional.designation}</p>
              </div>
            )}
            {registrationDataProfessional.specialties &&
              registrationDataProfessional.specialties.length > 0 && (
                <div>
                  <span className="text-muted-foreground">
                    Areas of Expertise:
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {registrationDataProfessional.specialties.map(
                      (specialty, index) => {
                        const specialtyId =
                          typeof specialty === "string"
                            ? specialty
                            : specialty.specialtyId;
                        return (
                          <Badge
                            key={specialtyId || index}
                            variant="secondary"
                            className="text-xs"
                          >
                            {specialtyTitleById.get(specialtyId) ?? specialtyId}
                          </Badge>
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            {registrationDataProfessional.skills &&
              registrationDataProfessional.skills.length > 0 && (
                <div>
                  <span className="text-muted-foreground">Skills:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {registrationDataProfessional.skills.map((skill) => (
                      <Badge
                        key={skill.skillId}
                        variant="secondary"
                        className="text-xs"
                      >
                        {skillTitleById.get(skill.skillId) ?? skill.skillId}
                        {skill.yearsOfExperience != null
                          ? ` · ${skill.yearsOfExperience}y`
                          : ""}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            {registrationDataProfessional.hourlyRateMin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-muted-foreground">
                    Min Hourly Rate:
                  </span>
                  <p>
                    {registrationDataProfessional.hourlyRateMin.currency}{" "}
                    {registrationDataProfessional.hourlyRateMin.amount}
                  </p>
                </div>
                {registrationDataProfessional.hourlyRateMax && (
                  <div>
                    <span className="text-muted-foreground">
                      Max Hourly Rate:
                    </span>
                    <p>
                      {registrationDataProfessional.hourlyRateMax.currency}{" "}
                      {registrationDataProfessional.hourlyRateMax.amount}
                    </p>
                  </div>
                )}
              </div>
            )}
            {registrationDataProfessional.maxTravelDistance && (
              <div>
                <span className="text-muted-foreground">
                  Max Travel Distance:
                </span>
                <p>{registrationDataProfessional.maxTravelDistance} km</p>
              </div>
            )}
            {registrationDataProfessional.licenseNumber && (
              <div>
                <span className="text-muted-foreground">License Number:</span>
                <p>{registrationDataProfessional.licenseNumber}</p>
              </div>
            )}
            {registrationDataProfessional.referralCode && (
              <div>
                <span className="text-muted-foreground">Referral Code:</span>
                <p className="font-mono tracking-widest">
                  {registrationDataProfessional.referralCode}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [
    registrationDataProfessional.firstName,
    registrationDataProfessional.lastName,
    registrationDataProfessional.phone,
    registrationDataProfessional.bio,
    registrationDataProfessional.designation,
    registrationDataProfessional.specialties,
    registrationDataProfessional.skills,
    skillTitleById,
    specialtyTitleById,
    registrationDataProfessional.hourlyRateMin,
    registrationDataProfessional.hourlyRateMax,
    registrationDataProfessional.maxTravelDistance,
    registrationDataProfessional.licenseNumber,
    registrationDataProfessional.referralCode,
    userInfo.email
  ]);

  const practiceNode = useMemo(() => {
    return (
      <div className="space-y-6">
        {/* PRACTICE INFORMATION SECTION */}
        <div className="space-y-4">
          <CardSubHeader title="PRACTICE INFORMATION" as="h4" />
          <div className="space-y-4 text-sm">
            {registrationDataPractice.practiceName && (
              <div>
                <span className="text-muted-foreground">Legal Name:</span>
                <p>{registrationDataPractice.practiceName}</p>
              </div>
            )}
            {registrationDataPractice.website && (
              <div>
                <span className="text-muted-foreground">Website:</span>
                <p>{registrationDataPractice.website}</p>
              </div>
            )}
            {registrationDataPractice.description && (
              <div>
                <span className="text-muted-foreground">Practice Bio:</span>
                <p className="mt-1">{registrationDataPractice.description}</p>
              </div>
            )}
            {registrationDataPractice.taxId && (
              <div>
                <span className="text-muted-foreground">Tax Id:</span>
                <p>{registrationDataPractice.taxId}</p>
              </div>
            )}
            {registrationDataPractice.licenseNumber && (
              <div>
                <span className="text-muted-foreground">License Number:</span>
                <p>{registrationDataPractice.licenseNumber}</p>
              </div>
            )}
          </div>
        </div>

        {/* MAIN BRANCH SECTION */}
        <div className="space-y-4">
          <CardSubHeader title="MAIN BRANCH" as="h4" />
          <div className="space-y-4 text-sm">
            {registrationDataPractice.branchName && (
              <div>
                <span className="text-muted-foreground">Display Name:</span>
                <p>{registrationDataPractice.branchName}</p>
              </div>
            )}
          </div>
        </div>

        {/* ADMIN INFORMATION SECTION */}
        <div className="space-y-4">
          <CardSubHeader title="ADMIN INFORMATION" as="h4" />
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">First Name:</span>
              <p>{registrationDataPractice.firstName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Name:</span>
              <p>{registrationDataPractice.lastName}</p>
            </div>
            {registrationDataPractice.designation && (
              <div>
                <span className="text-muted-foreground">Designation:</span>
                <p>{registrationDataPractice.designation}</p>
              </div>
            )}
            {registrationDataPractice.phone && (
              <div>
                <span className="text-muted-foreground">Mobile Number:</span>
                <p>{registrationDataPractice.phone}</p>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Email Address:</span>
              <p>{userInfo.email}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    registrationDataPractice.practiceName,
    registrationDataPractice.website,
    registrationDataPractice.description,
    registrationDataPractice.taxId,
    registrationDataPractice.licenseNumber,
    registrationDataPractice.branchName,
    registrationDataPractice.firstName,
    registrationDataPractice.lastName,
    registrationDataPractice.designation,
    registrationDataPractice.phone,
    userInfo.email
  ]);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-4">Review Your Information</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Please review your information before completing registration
        </p>
      </div>

      <div className="space-y-2">
        <h4 className="font-medium">Account Type</h4>
        <Badge variant="outline" className="capitalize">
          {selectedUserType}
        </Badge>
      </div>

      {/* Professional Node */}
      {selectedUserType === "professional" && professionalNode}

      {/* Practice Node */}
      {selectedUserType === "practice" && practiceNode}

      {/* Address Section */}
      <div className="space-y-4">
        <CardSubHeader title="ADDRESS INFORMATION" as="h4" />
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="col-span-2">
            <span className="text-muted-foreground">Street Address:</span>
            <p>
              {selectedUserType === "professional"
                ? registrationDataProfessional.addressStreet
                : registrationDataPractice.addressStreet}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">City:</span>
            <p>
              {selectedUserType === "professional"
                ? registrationDataProfessional.addressCity
                : registrationDataPractice.addressCity}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">State:</span>
            <p>
              {selectedUserType === "professional"
                ? registrationDataProfessional.addressState
                : registrationDataPractice.addressState}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Country:</span>
            <p>
              {selectedUserType === "professional"
                ? registrationDataProfessional.addressCountry
                : registrationDataPractice.addressCountry}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">ZIP Code:</span>
            <p>
              {selectedUserType === "professional"
                ? registrationDataProfessional.addressPostalCode
                : registrationDataPractice.addressPostalCode}
            </p>
          </div>
        </div>
      </div>

      {error ? (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md space-y-2">
          <p className="text-sm font-medium text-destructive">
            Registration could not be completed
          </p>
          <ul className="list-disc list-inside space-y-1">
            {getErrorMessages(error).map((message) => (
              <li key={message} className="text-sm text-destructive">
                {message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className={cn(components.buttonContinue)}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner
                  size="sm"
                  className="mr-2"
                  spinnerClassName="border-white"
                  showText={false}
                />
                Creating Account...
              </>
            ) : (
              "Complete Registration"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
});
